import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import './subscriptionoverview.scss';
import ProgressRing from '../../components/ProgressRing/ProgressRing';
import Button from '../../components/Button/Button';
import Modal from '../../components/Modal/Modal';
import { ReactComponent as CloseIcon } from '../../images/Close.svg';
import { formatNumber, randomColorAndLighter } from '../../utils/functions';
import base from '../../apis';
import { toast } from 'react-toastify';
import { Radio, TextInput, Select } from '@mantine/core';
import SearchIcon from '@mui/icons-material/Search';
import Spinner from '../../components/Spinner/Spinner';
import useTargets from '../../hooks/useTargets';
import useFetch from '../../hooks/useFetch';

const allowed = ['BPO-farm-agent', 'BPO-premier-agent', 'BPO-deal-analyzer', 'BPO-combo-package'];
const packMark = ['bpo-farm', 'premier-agent-website', 'deal-analyzer', 'combo-package'];

//get years from current year to next year
const years = [];
//get years from current to 3 years behind
for (let i = new Date().getFullYear(); i >= new Date().getFullYear() - 3; i--) {
  years.push(`${i}`);
}

const months = [
  {
    value: '1',
    label: 'January',
  },
  {
    value: '2',
    label: 'February',
  },
  {
    value: '3',
    label: 'March',
  },
  {
    value: '4',
    label: 'April',
  },
  {
    value: '5',
    label: 'May',
  },
  {
    value: '6',
    label: 'June',
  },
  {
    value: '7',
    label: 'July',
  },
  {
    value: '8',
    label: 'August',
  },
  {
    value: '9',
    label: 'September',
  },
  {
    value: '10',
    label: 'October',
  },
  {
    value: '11',
    label: 'November',
  },
  {
    value: '12',
    label: 'December',
  },
];

function SubscriptionOverview() {
  const { type } = useParams();
  const [durationSelected, setDurationSelected] = useState('year');
  const [isLoading, setIsLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [subsBackup, setSubsBackup] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState({
    year: `${new Date().getFullYear()}`,
    month: `${new Date().getMonth() + 1}`,
  });
  const { selectedYear, selectedMonth } = useTargets(selection.year, selection.month);
  const { data: subscriptions } = useFetch(
    `/compensations/operations-compensation-view-amount/?package=${
      packMark[allowed.findIndex((item) => item === type)]
    }&year=${selection.year}${durationSelected === 'month' ? `&month=${selection.month}` : ''}`
  );

  useFetch();

  useEffect(() => {
    const params = {
      package: packMark[allowed.findIndex((item) => item === type)],
      year: selection.year,
    };
    if (durationSelected === 'month') params.month = selection.month;
    setIsLoading(true);
    setSubs([]);
    setSubsBackup([]);
    base
      .get('compensations/operations-compensation-view/', {
        params,
      })
      .then(({ data }) => {
        if (!data.length) {
          toast.warn('No subscriptions found');
          return;
        }
        setIsGroup(false);
        setSearch('');
        setSubs(data);
        setSubsBackup(data);
        const groups = data
          .filter((item) => !!item.marketer)
          .reduce((acc, item) => {
            if (!acc?.find((el) => el.id === item.marketer.team_id)) {
              acc?.push({
                id: item.marketer.team_id,
                name: item.marketer.team,
                subs: [item],
              });
            } else {
              acc[acc.findIndex((el) => el.id === item.marketer.team_id)]?.subs.push(item);
            }
            return acc;
          }, []);
        setGroups(groups);
      })
      .catch((err) => {
        toast.error('Could not get data');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [type, durationSelected, selection.month, selection.year]);

  const [isGroup, setIsGroup] = useState(false);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const title = type
    .split('-')
    .map((item) => item[0].toUpperCase() + item.slice(1))
    .join(' ');

  const [selectedSub, setSelectedSub] = useState(null);
  const handleClick = (sub) => {
    setSelectedSub(sub);
    setDetailsModalOpen(true);
  };

  const changeGroupBy = () => {
    setIsGroup((val) => {
      if (!val) {
        setSubsBackup(subs);
      }
      return !val;
    });
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    let filtered = [];
    if (!value) {
      filtered = [...subsBackup];
    } else {
      filtered = subsBackup.filter((item) =>
        `${item.agent.user.first_name.toLowerCase()} ${item.agent.user.first_name.toLowerCase()}`.includes(
          value.toLowerCase()
        )
      );
    }
    setSubs(filtered);
  };

  if (!allowed.includes(type)) {
    //todo change dashboard to operations team's dashboard
    return <Navigate to="/404" replace state={{ from: '/dashboard' }} />;
  }

  return (
    <>
      {isLoading && <Spinner />}
      <Modal open={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} style={{ minWidth: 'unset' }}>
        <div className="mod2">
          <div className="mod2header">
            <div>
              <p>{`${selectedSub?.agent?.user?.first_name} ${selectedSub?.agent?.user?.last_name}`}</p>
            </div>
            <CloseIcon
              onClick={() => setDetailsModalOpen(false)}
              style={{ fill: '#1378A5', cursor: 'pointer' }}
              width="18px"
              height="18px"
            />{' '}
          </div>
          <div className="contholder" style={{ padding: 'unset' }}>
            <div>
              <p>
                Agent's Name:{' '}
                <span>{`${selectedSub?.agent?.user?.first_name} ${selectedSub?.agent?.user?.last_name}`}</span>
              </p>
              <p>
                Agent's Email: <span>{selectedSub?.agent?.user?.email}</span>
              </p>
              <p style={{ whiteSpace: 'break-spaces' }}>
                Date of Subscription : {new Date(selectedSub?.subscription?.current_period_start).toDateString()}
              </p>
            </div>
            <div>
              <p>
                Current Mkt Exe:
                <span> {selectedSub?.marketer?.user?.first_name + ' ' + selectedSub?.marketer?.user?.last_name}</span>
              </p>
              <p>
                Assigned Team: <span>{selectedSub?.marketer?.team}</span>
              </p>
            </div>
          </div>
        </div>
      </Modal>
      <div className="subscriptionoverview">
        <div className="subscriptionoverview__header">
          <div>
            <div>
              <div>
                <Radio.Group name="durationSelected" value={durationSelected} onChange={setDurationSelected}>
                  <Radio value="year" label="Yearly" />
                  <Radio value="month" label="Monthly" />
                </Radio.Group>
              </div>
            </div>
          </div>
          <div>
            <Select
              data={years}
              placeholder="Year"
              value={selection.year}
              onChange={(val) => setSelection({ ...selection, year: val })}
            />

            {['month'].includes(durationSelected) && (
              <Select
                data={months}
                placeholder="Month"
                value={selection.month}
                onChange={(val) => setSelection({ ...selection, month: val })}
              />
            )}
          </div>
        </div>
        <div className="subscriptionoverview__info">
          <div>
            <ProgressRing
              current={subscriptions?.package_amount || 0}
              total={
                { ...(durationSelected === 'month' ? selectedMonth : selectedYear) }?.sub_targets?.find(
                  (item) => item.package_name === packMark[allowed.findIndex((item) => item === type)]
                )?.amount || 0
              }
              unitOfMeasure="$"
            />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: '200px' }}>
                <div>
                  <p className="bolden">$ {formatNumber(subscriptions?.package_amount || 0)}</p>
                  <span className="lighh">Total sales made this {durationSelected}</span>
                </div>
                {/* <div>
                  <p className="bolden">60 points</p>
                  <span className="lighh">Total points gained this {durationSelected}</span>
                </div> */}
              </div>
              <hr />
              <div style={{ display: 'flex', gap: '100px' }}>
                <div>
                  <p className="bolden">
                    ${' '}
                    {formatNumber(
                      { ...(durationSelected === 'month' ? selectedMonth : selectedYear) }?.sub_targets?.find(
                        (item) => item.package_name === packMark[allowed.findIndex((item) => item === type)]
                      )?.amount || 0
                    )}
                  </p>
                  <span className="lighh">Sales Target</span>
                </div>
                {/* <div>
                  <p className="bolden">150 points</p>
                  <span className="lighh">Points Target</span>
                </div> */}
              </div>
            </div>
          </div>
          <div className="subscriptionoverview__info-content">
            <h3>Target for the {durationSelected}</h3>
          </div>
        </div>
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '20px',
            }}>
            <h3>{title} Subsciptions</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <TextInput
                placeholder="Enter a name"
                type="search"
                rightSection={<SearchIcon sx={{ cursor: 'pointer' }} />}
                value={search}
                onChange={handleSearch}
              />
              <Button
                style={{
                  width: '200px',
                  padding: '0px 30px',
                  color: 'white',
                  fontSize: '16px',
                  backgroundColor: '#FFAA01',
                  border: 'none',
                  margin: 0,
                }}
                onClick={changeGroupBy}
                text={isGroup ? 'Default Groupings' : 'Group By Teams'}
              />
            </div>
          </div>
          <div>
            {!isGroup ? (
              <>
                {subs.map((sub) => (
                  <SubscriptionCard
                    key={sub.id}
                    name={`${sub?.agent?.user?.first_name} ${sub?.agent?.user?.last_name}`}
                    email={`${sub?.agent?.user?.email}`}
                    date={new Date(sub?.subscription?.current_period_start).toDateString()}
                    onClick={() => handleClick(sub)}
                  />
                ))}
              </>
            ) : (
              <>
                {groups.map((group) => (
                  <Expander name={group.name} key={group.id} count={group.subs.length}>
                    {group.subs.map((sub) => (
                      <SubscriptionCard
                        key={sub.id}
                        name={`${sub?.agent?.user?.first_name} ${sub?.agent?.user?.last_name}`}
                        email={`${sub?.agent?.user?.email}`}
                        date={new Date(sub?.subscription?.current_period_start).toDateString()}
                        onClick={() => handleClick(sub)}
                      />
                    ))}
                  </Expander>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SubscriptionOverview;

const SubscriptionCard = ({ onClick, name, email, date }) => {
  return (
    <div className="subscriptions_wrapper" onClick={onClick}>
      <div className="card">
        <p>{name}</p>
        <p>{email}</p>
        <p>{date}</p>
      </div>
    </div>
  );
};

const Expander = ({ name, count, children }) => {
  const constantColor = React.useRef(randomColorAndLighter().color);
  const [open, setOpen] = React.useState(false);
  return (
    <div className="expander" onClick={() => setOpen((val) => !val)}>
      <div className="expander__header">
        <span className="expander__header-circle" style={{ backgroundColor: constantColor.current }}></span>
        <span>{name}</span>
        <span className="expander__header-sub">{count} subscriptions</span>
      </div>
      {open && <div className="expander__content">{children}</div>}
      {/* {open&&children} */}
    </div>
  );
};
